import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Home, Images, CalendarDays, Users, User, LogOut, Menu, X, Sparkles, Trees } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { API_BASE } from '@/lib/api';
import NotificationBell from '@/components/NotificationBell';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', icon: Home, label: 'Feed' },
    { path: '/albums', icon: Images, label: 'Albums' },
    { path: '/calendar', icon: CalendarDays, label: 'Calendar' },
    { path: '/tree', icon: Trees, label: 'Tree' },
    { path: '/family', icon: Users, label: 'Family' },
  ];

  const isActive = (path) => location.pathname === path;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="header">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo — large, no container */}
          <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="Kinsy home">
            <img
              src={LOGO_URL}
              alt="Kinsy"
              className="h-12 sm:h-14 w-auto select-none"
              draggable="false"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-0.5 lg:gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                aria-label={item.label}
                title={item.label}
                className={`flex items-center gap-2 px-2.5 lg:px-4 py-2 rounded-full font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {/* Labels hidden on md (768–1023px) to keep the header
                    on ONE row when the notification bell + avatar are
                    also fighting for space (see iPad layout report).
                    Screen readers still read the label from `aria-label`. */}
                <span className="hidden lg:inline">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-1 sm:gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                <button
                  type="button"
                  className="flex items-center gap-2 p-1.5 rounded-full hover:bg-stone-100 active:bg-stone-200 transition-colors min-w-[44px] min-h-[44px]"
                  aria-label="Open account menu"
                >
                  <Avatar className="h-10 w-10 border-2 border-teal-100 pointer-events-none">
                    {user?.avatar && (
                      <AvatarImage
                        src={`${API_BASE}/files/${user.avatar}`}
                        alt={user?.name}
                        className="object-cover"
                      />
                    )}
                    <AvatarFallback className="bg-teal-600 text-white font-medium">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-2xl">
                <div className="px-3 py-2">
                  <p className="font-medium text-stone-900">{user?.name}</p>
                  <p className="text-sm text-stone-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="profile-link">
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/pricing" className="flex items-center gap-2 cursor-pointer" data-testid="pricing-link">
                    <Sparkles className="w-4 h-4 text-teal-600" />
                    <span>Upgrade plan</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout}
                  className="flex items-center gap-2 text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-full hover:bg-stone-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-stone-200" data-testid="mobile-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
            <div className="px-4 pt-3 mt-2 border-t border-stone-100 text-xs text-stone-400" data-testid="app-version">
              v2.3 · Album upload fix
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
